import React from 'react';
import { Hash, ListFilter, Shuffle } from 'lucide-react';

/**
 * Компонент для отображения основных опций генератора
 */
const SimpleOptionsSection = ({ formData, handleInputChange, errors }) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
      {/* Диапазон значений (мин и макс) */}
      <div className="col-span-full">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
          <Hash className="w-4 h-4 mr-2 text-indigo-500" />
          Диапазон чисел
        </label>
        <div className="flex gap-2">
          <div className="flex-1">
            <input
              type="text"
              id="min"
              name="min"
              value={formData.min}
              onChange={handleInputChange}
              className={`form-input w-full ${errors.min ? 'input-error' : ''}`}
              placeholder="Минимум"
              aria-label="Минимальное значение"
            />
            {errors.min && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.min}
              </p>
            )}
          </div>
          <div className="flex-1">
            <input
              type="text"
              id="max"
              name="max"
              value={formData.max}
              onChange={handleInputChange}
              className={`form-input w-full ${errors.max ? 'input-error' : ''}`}
              placeholder="Максимум"
              aria-label="Максимальное значение"
            />
            {errors.max && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.max}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Пользовательские диапазоны */}
      <div className="col-span-full">
        <div className="flex items-center">
          <input
            type="checkbox"
            id="customRange"
            name="customRange"
            checked={formData.customRange}
            onChange={handleInputChange}
            className="form-checkbox"
          />
          <label htmlFor="customRange" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
            Использовать разные диапазоны чисел
          </label>
        </div>
        
        {formData.customRange && (
          <div className="mt-2">
            <input
              type="text"
              id="customRangeValues"
              name="customRangeValues"
              value={formData.customRangeValues}
              onChange={handleInputChange}
              className={`form-input w-full ${errors.customRangeValues ? 'input-error' : ''}`}
              placeholder="Например: 1-10:3,50-100:2"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Формат: мин-макс:количество, через запятую. Например, 1-10:3,50-100:2 сгенерирует 3 числа из диапазона 1-10 и 2 числа из диапазона 50-100.
            </p>
            {errors.customRangeValues && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.customRangeValues}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Количество чисел */}
      <div>
        <label htmlFor="count" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
          <ListFilter className="w-4 h-4 mr-2 text-indigo-500" />
          Количество чисел
        </label>
        <input
          type="text"
          id="count"
          name="count"
          value={formData.count}
          onChange={handleInputChange}
          className={`form-input w-full ${errors.count ? 'input-error' : ''}`}
          placeholder="Сколько чисел сгенерировать"
        />
        {errors.count && (
          <p className="mt-1 text-xs text-red-500 dark:text-red-400">
            {errors.count}
          </p>
        )}
      </div>

      {/* Количество десятичных знаков */}
      <div>
        <label htmlFor="decimalPlaces" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Десятичные знаки
        </label>
        <input
          type="text"
          id="decimalPlaces"
          name="decimalPlaces"
          value={formData.decimalPlaces}
          onChange={handleInputChange}
          className={`form-input w-full ${errors.decimalPlaces ? 'input-error' : ''}`}
          placeholder="0 для целых чисел"
        />
        {errors.decimalPlaces && (
          <p className="mt-1 text-xs text-red-500 dark:text-red-400">
            {errors.decimalPlaces}
          </p>
        )}
      </div>

      {/* Сортировка */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
          <Shuffle className="w-4 h-4 mr-2 text-indigo-500" />
          Сортировка
        </label>
        <select
          id="sorting"
          name="sorting"
          value={formData.sorting}
          onChange={handleInputChange}
          className="form-select w-full"
        >
          <option value="none">Без сортировки</option>
          <option value="asc">По возрастанию</option>
          <option value="desc">По убыванию</option>
        </select>
      </div>

      {/* Уникальность */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Уникальность
        </label>
        <div className="flex items-center h-10">
          <input
            type="checkbox"
            id="unique"
            name="unique"
            checked={formData.unique}
            onChange={handleInputChange}
            className="form-checkbox"
          />
          <label htmlFor="unique" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
            Только уникальные числа
          </label>
        </div>
      </div>

      {/* Распределение */}
      <div className="col-span-full">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Тип распределения
        </label>
        <div className="grid grid-cols-3 gap-2">
          <label className="relative inline-flex items-center">
            <input
              type="radio"
              name="distribution"
              value="uniform"
              checked={formData.distribution === 'uniform'}
              onChange={handleInputChange}
              className="form-radio"
            />
            <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              Равномерное
            </span>
          </label>
          <label className="relative inline-flex items-center">
            <input
              type="radio"
              name="distribution"
              value="normal"
              checked={formData.distribution === 'normal'}
              onChange={handleInputChange}
              className="form-radio"
            />
            <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              Нормальное
            </span>
          </label>
          <label className="relative inline-flex items-center">
            <input
              type="radio"
              name="distribution"
              value="weighted"
              checked={formData.distribution === 'weighted'}
              onChange={handleInputChange}
              className="form-radio"
            />
            <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              Взвешенное
            </span>
          </label>
        </div>
      </div>
    </div>
  );
};

export default SimpleOptionsSection;